"use client";

import { Amiri, Montserrat, Playfair_Display, Roboto_Condensed, Roboto_Serif } from "next/font/google";
import Image, { getImageProps } from "next/image";
import { useLocale, useTranslations } from "next-intl";
import type { CSSProperties } from "react";

import Modal from "@/components/ui/Modal";
import { cn } from "@/lib/utils";
import calendarDotsMobile from "@/public/assets/images/escape-room/calendar-dots-mobile.svg";
import calendarDots from "@/public/assets/images/escape-room/calendar-dots.svg";
import calendarFrameMobile from "@/public/assets/images/escape-room/calendar-frame-mobile.svg";
import calendarFrame from "@/public/assets/images/escape-room/calendar-frame.svg";
import closeIconMobile from "@/public/assets/images/escape-room/close-mobile.svg";
import closeIcon from "@/public/assets/images/escape-room/close.svg";
import keyIconMobile from "@/public/assets/images/escape-room/key-mobile.svg";
import keyIcon from "@/public/assets/images/escape-room/key.svg";
import ticketShapeMobile from "@/public/assets/images/escape-room/ticket-mobile.svg";
import ticketShape from "@/public/assets/images/escape-room/ticket.svg";
import posterAr from "@/public/assets/images/halloween-escape-popup-ar.png";
import posterEn from "@/public/assets/images/halloween-escape-popup.jpg";
import heroAr from "@/public/assets/images/halloween-pop-up-mobile-ar.png";
import heroEn from "@/public/assets/images/halloween-pop-up-mobile-en.png";

// Fonts from the Figma design, fetched only when the modal renders. Arabic swaps the Latin-only faces for
// Amiri (headline) and the locale's Cairo/Tajawal (font-urbanist/font-inter).
const playfair = Playfair_Display({ subsets: ["latin"], weight: "700", display: "swap", preload: false });
const montserrat = Montserrat({ subsets: ["latin"], weight: ["600", "700"], display: "swap", preload: false });
const robotoCondensed = Roboto_Condensed({ subsets: ["latin"], weight: "400", display: "swap", preload: false });
const robotoSerif = Roboto_Serif({ subsets: ["latin"], weight: "700", display: "swap", preload: false });
const amiri = Amiri({ subsets: ["arabic"], weight: "700", display: "swap", preload: false });

type EscapeRoomOfferModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const EscapeRoomOfferModal = ({ isOpen, onClose }: EscapeRoomOfferModalProps) => {
  const t = useTranslations("Home.offerPopup");
  const locale = useLocale();
  const isArabic = locale === "ar";
  const poster = isArabic ? posterAr : posterEn;
  const hero = isArabic ? heroAr : heroEn;
  const bodyFont = isArabic ? "font-inter" : robotoCondensed.className;
  const labelFont = isArabic ? "font-urbanist tracking-normal md:tracking-normal" : montserrat.className;

  // Wide hero on mobile, portrait poster from md up; the browser only downloads the one it shows
  const {
    props: { srcSet: posterSrcSet },
  } = getImageProps({ src: poster, alt: "", fill: true, sizes: "520px" });
  const { props: heroProps } = getImageProps({
    src: hero,
    alt: t("imageAlt"),
    fill: true,
    sizes: "100vw",
    loading: "eager",
    fetchPriority: "high",
  });
  const imageAspects = {
    "--hero-aspect": `${hero.width} / ${hero.height}`,
    "--poster-aspect": `${poster.width} / ${poster.height}`,
  } as CSSProperties;

  const steps = [
    { text: t("steps.one"), className: "" },
    // Bar between steps 01 and 02 sits in the column gap: 40 + 2 + 40 on mobile, 28 + 2 + 28 on desktop
    {
      text: t("steps.two"),
      className:
        "before:absolute before:start-[calc(var(--u)*-42)] before:top-[calc(var(--u)*3)] before:h-[calc(var(--u)*68)] before:w-[calc(var(--u)*2)] before:bg-[#72071A] before:opacity-85 md:before:start-[calc(var(--u)*-30)] md:before:top-0 md:before:h-[calc(var(--u)*72)]",
    },
    // Divider above step 03: 16px gap (26px desktop), 2px rule, 30px gap
    {
      text: t("steps.three"),
      className:
        "col-span-2 mt-[calc(var(--u)*48)] before:absolute before:start-0 before:top-[calc(var(--u)*-32)] before:h-[calc(var(--u)*2)] before:w-[calc(var(--u)*954)] before:bg-[#B8894A]/80 md:mt-[calc(var(--u)*58)] md:max-w-[calc(var(--u)*620)] md:before:w-[calc(var(--u)*786)]",
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      // --u is one Figma pixel: the mobile design is 1086px wide, the desktop one 1792px (two 896px halves)
      className="!w-[calc(100%-1.5rem)] !max-w-[1040px] overflow-hidden !rounded-4xl !bg-[#F7EEE4] !p-0 [--u:calc(100cqw/1086)] [container-type:inline-size] md:[--u:calc(100cqw/1792)]"
      childrenClassName="!max-h-[calc(100dvh-2rem)]"
      closeButtonClassName="absolute end-[calc(var(--u)*43)] top-[calc(var(--u)*35)] z-10 size-[max(calc(var(--u)*28),20px)] cursor-pointer transition-opacity hover:opacity-70 md:end-[calc(var(--u)*30)] md:top-[calc(var(--u)*29)] md:size-[max(calc(var(--u)*36),24px)]"
      closeIcon={
        <>
          <Image src={closeIconMobile} alt="" className="size-full md:hidden" />
          <Image src={closeIcon} alt="" className="hidden size-full md:block" />
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div
          className="relative aspect-[var(--hero-aspect)] w-full bg-[#1A120C] md:aspect-[var(--poster-aspect)] md:h-full md:self-stretch"
          style={imageAspects}
        >
          <picture key={poster.src}>
            <source media="(min-width: 768px)" srcSet={posterSrcSet} sizes="520px" />
            {/* eslint-disable-next-line @next/next/no-img-element -- art-directed via getImageProps */}
            <img {...heroProps} alt={t("imageAlt")} className="object-cover object-center" />
          </picture>
        </div>

        <div className="flex flex-col items-center px-[calc(var(--u)*66)] pb-[calc(var(--u)*53)] pt-[calc(var(--u)*52)] text-center text-[#15161C] md:items-start md:pb-[calc(var(--u)*34)] md:pe-[calc(var(--u)*48)] md:ps-[calc(var(--u)*62)] md:pt-[calc(var(--u)*74)] md:text-start">
          <p
            className={cn(
              "flex items-center gap-[calc(var(--u)*24)] text-[length:max(calc(var(--u)*26),10px)] font-bold uppercase leading-normal tracking-[0.12em] text-[#72071A] md:text-[length:max(calc(var(--u)*22),10px)] md:tracking-[0.16em]",
              labelFont,
              isArabic && "text-[length:max(calc(var(--u)*33),11px)] md:text-[length:max(calc(var(--u)*28),11px)]",
            )}
          >
            <span className="hidden size-[calc(var(--u)*20)] shrink-0 bg-[#72071A] md:block" aria-hidden />
            {t("eyebrow")}
          </p>

          <h2
            className={cn(
              "mt-[calc(var(--u)*26)] whitespace-nowrap font-bold md:mt-[calc(var(--u)*22)]",
              isArabic
                ? cn(
                    amiri.className,
                    "text-[length:calc(var(--u)*88)] leading-[1.3] md:text-[length:calc(var(--u)*100)]",
                  )
                : cn(
                    playfair.className,
                    "text-[length:calc(var(--u)*96)] leading-[0.88] tracking-[-0.04em] md:text-[length:calc(var(--u)*112)] md:tracking-[-0.03em]",
                  ),
            )}
          >
            {/* Two lines on mobile, three on desktop */}
            <span className="block">{t("titleLineOne")}</span>
            <span className="block">
              <span className="md:block">{t("titleLineTwo")}</span>{" "}
              <span className="text-[#72071A] md:block">{t("titleAccent")}</span>
            </span>
          </h2>

          <div className="relative mt-[calc(var(--u)*32)] aspect-[954/194] w-[calc(var(--u)*954)] md:mt-[calc(var(--u)*34)] md:aspect-[786/269] md:w-[calc(var(--u)*786)]">
            <Image src={ticketShapeMobile} alt="" fill className="pointer-events-none md:hidden" />
            <Image src={ticketShape} alt="" fill className="pointer-events-none hidden md:block" />

            <div className="relative flex h-full flex-col justify-center gap-[calc(var(--u)*8)] ps-[calc(var(--u)*57)] text-start md:gap-[calc(var(--u)*10)] md:pt-[calc(var(--u)*6)]">
              <p
                className={cn(
                  "whitespace-nowrap text-[length:max(calc(var(--u)*21),8px)] font-semibold uppercase leading-normal tracking-[0.16em] text-[#B8894A] md:text-[length:max(calc(var(--u)*17),8px)] md:tracking-[0.18em]",
                  labelFont,
                  isArabic && "text-[length:max(calc(var(--u)*28),9px)] md:text-[length:max(calc(var(--u)*23),9px)]",
                )}
              >
                {t("kicker")}
              </p>
              <p
                className={cn(
                  "whitespace-nowrap font-inter text-[length:calc(var(--u)*70)] font-bold md:max-w-[calc(var(--u)*398)] md:whitespace-normal md:text-[length:calc(var(--u)*66)]",
                  isArabic ? "leading-[1.15]" : "leading-none tracking-[-0.03em] md:leading-[0.95]",
                )}
              >
                {t("ticketTitle")}
              </p>
              <p
                className={cn(
                  "text-[length:calc(var(--u)*34)] leading-normal md:text-[length:calc(var(--u)*31)]",
                  bodyFont,
                )}
              >
                {t("ticketSubtitle")}
              </p>
            </div>

            <Image
              src={keyIconMobile}
              alt=""
              className="absolute end-[calc(var(--u)*72.5)] top-[calc(var(--u)*25.4)] h-[calc(var(--u)*156.3)] w-[calc(var(--u)*63.1)] md:hidden"
            />
            <Image
              src={keyIcon}
              alt=""
              className="absolute end-[calc(var(--u)*87.5)] top-[calc(var(--u)*56.5)] hidden h-[calc(var(--u)*163)] w-[calc(var(--u)*65)] md:block"
            />
          </div>

          <p
            className={cn(
              "mt-[calc(var(--u)*36)] flex items-center gap-[calc(var(--u)*36)] text-[length:calc(var(--u)*32)] leading-normal md:mt-[calc(var(--u)*24)] md:gap-[calc(var(--u)*40)] md:ps-[calc(var(--u)*5)] md:text-[length:calc(var(--u)*33)]",
              bodyFont,
            )}
          >
            <span
              className="relative h-[calc(var(--u)*46)] w-[calc(var(--u)*42)] shrink-0 md:h-[calc(var(--u)*44)] md:w-[calc(var(--u)*40)]"
              aria-hidden
            >
              <Image
                src={calendarFrameMobile}
                alt=""
                className="absolute left-[calc(var(--u)*0.5)] top-[calc(var(--u)*1)] h-[calc(var(--u)*44)] w-[calc(var(--u)*41)] md:hidden"
              />
              <Image
                src={calendarDotsMobile}
                alt=""
                className="absolute left-[calc(var(--u)*8.45)] top-[calc(var(--u)*20.9)] h-[calc(var(--u)*18.8)] w-[calc(var(--u)*26.1)] md:hidden"
              />
              <Image
                src={calendarFrame}
                alt=""
                className="absolute left-[calc(var(--u)*0.4)] top-[calc(var(--u)*1)] hidden h-[calc(var(--u)*42.1)] w-[calc(var(--u)*39.2)] md:block"
              />
              <Image
                src={calendarDots}
                alt=""
                className="absolute left-[calc(var(--u)*8)] top-[calc(var(--u)*20)] hidden h-[calc(var(--u)*18)] w-[calc(var(--u)*25)] md:block"
              />
            </span>
            {t("dates")}
          </p>

          <span
            className="mt-[calc(var(--u)*36)] h-[calc(var(--u)*2)] w-[calc(var(--u)*954)] shrink-0 bg-[#B8894A]/80 md:mt-[calc(var(--u)*26)] md:w-[calc(var(--u)*786)]"
            aria-hidden
          />

          <ol className="mt-[calc(var(--u)*16)] grid grid-cols-[calc(var(--u)*436)_calc(var(--u)*436)] items-center gap-x-[calc(var(--u)*82)] text-start md:mt-[calc(var(--u)*26)] md:grid-cols-[calc(var(--u)*360)_calc(var(--u)*368)] md:items-start md:gap-x-[calc(var(--u)*58)]">
            {steps.map((step, index) => (
              <li key={step.text} className={cn("relative flex items-center gap-[calc(var(--u)*30)]", step.className)}>
                <span
                  className={cn(
                    robotoSerif.className,
                    "text-[length:calc(var(--u)*40)] font-bold leading-normal text-[#72071A]",
                  )}
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span
                  className="h-[calc(var(--u)*72)] w-[calc(var(--u)*2)] shrink-0 bg-[#72071A] opacity-85"
                  aria-hidden
                />
                <p
                  className={cn(
                    "min-w-0 flex-1 text-[length:calc(var(--u)*31)] leading-[1.2]",
                    // Mobile design breaks steps 01/02 after "Your" / "voucher"
                    index < 2 && "max-w-[calc(var(--u)*265)] md:max-w-none",
                    bodyFont,
                  )}
                >
                  {step.text}
                </p>
              </li>
            ))}
          </ol>

          <p
            className={cn(
              "mt-auto self-center pt-[calc(var(--u)*51)] text-[length:max(calc(var(--u)*24),10px)] leading-normal md:pt-[calc(var(--u)*87)]",
              bodyFont,
            )}
          >
            {t("terms")}
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default EscapeRoomOfferModal;
